import React, { useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { FaTimes } from "react-icons/fa";
import { handleTokenAndPostRequest } from "../utils/utils";

const TOPUP_PRESETS = [10, 25, 50, 100, 250];
const BRAND_ACCENT = "#10b981";
const GRAY_DARK = "#374151";
const WHITE = "#fff";

// Reusable "Buy extra usage" modal — same UI as the one in pages/Billing.js,
// extracted so other surfaces (e.g. the Save Note modal in PropertySearchDetail)
// can pop it on top when the user is short on credit.
export default function BuyExtraUsageModal({
  onClose,
  defaultAmount = 25,
  returnUrl,
  reason,
  zIndex = 1100,
}) {
  const [topupAmount, setTopupAmount] = useState(defaultAmount);
  const [topupLoading, setTopupLoading] = useState(false);

  const handleBuyCredit = async () => {
    setTopupLoading(true);
    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/billing/create-topup-checkout`,
        {
          amount: Number(topupAmount),
          return_url: returnUrl || `${window.location.origin}/billing`,
        }
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.error || "Could not start checkout");
    } finally {
      setTopupLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: WHITE,
          borderRadius: 14,
          maxWidth: 460,
          width: "100%",
          padding: 24,
          position: "relative",
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#9ca3af",
            fontSize: 16,
            padding: 4,
          }}
        >
          <FaTimes />
        </button>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1f2937", margin: "0 0 6px 0" }}>
          Buy extra usage
        </h3>
        {reason && (
          <p
            style={{
              fontSize: 13,
              color: "#92400e",
              background: "#fef3c7",
              border: "1px solid #fde68a",
              padding: "8px 10px",
              borderRadius: 8,
              margin: "10px 0 12px",
              lineHeight: 1.5,
            }}
          >
            {reason}
          </p>
        )}
        <p style={{ fontSize: "0.85rem", color: "#6b7280", marginBottom: 14 }}>
          Choose how much credit to add. You'll be redirected to Stripe to enter
          your card details.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {TOPUP_PRESETS.map((amt) => (
            <button
              key={amt}
              onClick={() => setTopupAmount(amt)}
              style={{
                flex: "1 1 60px",
                padding: "10px 0",
                borderRadius: 8,
                border: `1px solid ${Number(topupAmount) === amt ? BRAND_ACCENT : "#e2e8f0"}`,
                background: Number(topupAmount) === amt ? "rgba(16,185,129,0.08)" : WHITE,
                color: Number(topupAmount) === amt ? BRAND_ACCENT : GRAY_DARK,
                fontWeight: 700,
                fontSize: "0.9rem",
                cursor: "pointer",
              }}
            >
              A${amt}
            </button>
          ))}
        </div>
        <label style={{ fontSize: "0.8rem", color: "#6b7280", display: "block", marginBottom: 4 }}>
          Or enter a custom amount (AUD)
        </label>
        <input
          type="number"
          min={5}
          max={1000}
          step={1}
          value={topupAmount}
          onChange={(e) => setTopupAmount(e.target.value)}
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
            fontSize: "0.95rem",
            marginBottom: 18,
            boxSizing: "border-box",
          }}
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              padding: "10px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: WHITE,
              color: GRAY_DARK,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleBuyCredit}
            disabled={topupLoading || !topupAmount || Number(topupAmount) < 5}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "none",
              background: BRAND_ACCENT,
              color: WHITE,
              fontWeight: 700,
              cursor: topupLoading ? "wait" : "pointer",
              minWidth: 120,
            }}
          >
            {topupLoading ? <Spinner animation="border" size="sm" /> : `Buy A$${Number(topupAmount || 0).toFixed(0)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
